import { useMutation, useQueryClient } from '@tanstack/react-query';
import { profileApi } from '@/services';
import type { TProfile, TUser } from '@/services/auth/auth.types.ts';

type TUpdateProfileError = { data: { message: string[] } };

type TUseUpdateProfileOptions = {
  onSuccess?: (data: TProfile) => void;
  onError?: (error: TUpdateProfileError) => void;
};

/**
 * Shared profile-update mutation: PATCHes the profile and patches the
 * ['profile','getUserProfile','info'] and ['currentUser'] caches so the
 * profile_completed gate reacts immediately. Used by the onboarding wizard
 * and the My Profile tab. Notifications and navigation stay in the callers.
 *
 * Note: callers must send only address + phone fields. The backend nulls
 * profile.region whenever a PATCH omits it (pre-existing behavior), so do
 * not extend the payload here without addressing that server-side.
 */
export const useUpdateProfile = (options?: TUseUpdateProfileOptions) => {
  const queryClient = useQueryClient();

  const { mutate: updateProfile, isPending } = useMutation({
    mutationFn: profileApi.updateProfile,
    onSuccess: (data) => {
      const patchProfile = (oldData: TUser) => ({
        ...oldData,
        profile: {
          ...oldData?.profile,
          profile_completed: data.profile_completed,
          property_address: data.property_address,
          property_apt_unit: data.property_apt_unit,
          property_city: data.property_city,
          property_state: data.property_state,
          property_zip_code: data.property_zip_code,
          phone_number: data.phone_number,
          contact: data.contact,
        },
      });

      queryClient.setQueryData(['profile', 'getUserProfile', 'info'], patchProfile);
      queryClient.setQueryData(['currentUser'], patchProfile);

      options?.onSuccess?.(data);
    },
    onError: (error: TUpdateProfileError) => {
      options?.onError?.(error);
    },
  });

  return { updateProfile, isPending };
};
